import { 
  FaRegClock, 
  FaArrowRotateRight, 
  FaRegAddressCard, 
  FaCheck
} from 'react-icons/fa6'
import { MdOutlineFace, MdPhonelinkRing } from 'react-icons/md'
import { LuShieldCheck } from 'react-icons/lu'

export type VerificationStage = 'notStarted' | 'inProgress' | 'completed'

type StageCardProps = {
  disabled: boolean
  onVerify: () => void
}

const NoStartedIDVerification = ({ disabled, onVerify }: StageCardProps) => (
  <div className="card my-3" style={{ transform: 'none' }}>
    <div className="card-header bg-transparent">
      <h4 className="m-0">Identity Verification</h4>
    </div>
    <div className="card-body ">
      <p className="text-muted">
        We use a secure identity verification service to confirm your information quickly
        and securely. It helps us meet banking regulations while protecting customers from identity fraud.
      </p>
      <span className="fw-bold">What you'll need:</span>
      <div className="d-flex flex-sm-row flex-column justify-content-between gap-3">
        <div className="d-flex flex-column gap-3 mt-3 text-muted">
          <div className="d-flex align-items-center gap-3">
            <div className="text-primary bg-primary-subtle rounded-3 p-1">
              <FaRegAddressCard size={18} />
            </div>
            <span>Driver's license or government-issued ID</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="text-primary bg-primary-subtle rounded-3 p-1">
              <MdOutlineFace size={18} />
            </div>
            <span>A quick selfie</span>
          </div>
        </div>
        <div className="d-flex flex-row justify-content-end align-items-center align-self-end gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm d-flex align-items-center gap-2"
            disabled={disabled}
            onClick={onVerify}
          >
            <LuShieldCheck />
            Verify Identity
          </button>
        </div>
      </div>
    </div>
  </div>
)

const StartedIDVerification = ({ disabled, onVerify }: StageCardProps) => (
  <div className="card my-3" style={{ transform: 'none' }}>
    <div className="card-header bg-transparent">
      <div className="d-flex flex-row justify-content-between align-items-center">
        <h4 className="m-0">Identity Verification</h4>
        <div className="bg-warning-subtle text-warning-emphasis badge rounded-pill px-2 py-1 d-flex align-items-center gap-2 fw-medium">
          <FaRegClock size={12} />
          <span>Pending</span>
        </div>
      </div>
    </div>
    <div className="card-body">
      <span className="fw-bold">Check your phone:</span>
      <div className="mt-3">
        <div className="d-flex flex-row gap-3">
          <div className="text-warning-emphasis bg-warning-subtle rounded-circle p-2" >
            <MdPhonelinkRing size={24} />
          </div>
          <p className="text-muted m-0">
            We sent a secure verification link to your phone via text.
            Follow the link and upload your government-issued ID and take a selfie.
          </p>
        </div>
        <div className="d-flex flex-row justify-content-end align-items-center align-self-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-outline-primary border-primary btn-sm d-flex align-items-center gap-2"
            disabled={disabled}
            onClick={onVerify}
          >
            <FaArrowRotateRight />
            Resend Link
          </button>
        </div>
      </div>
    </div>
  </div>
)

const CompletedIDVerification = () => (
  <div className="card my-3" style={{ transform: 'none' }}>
    <div className="card-header bg-transparent">
      <div className="d-flex flex-row justify-content-between align-items-center">
        <h4 className="m-0">Identity Verification</h4>
        <div className="bg-success-subtle text-success-emphasis badge rounded-pill px-2 py-1 d-flex align-items-center gap-2 fw-medium">
          <FaCheck size={12} />
          <span>Completed</span>
        </div>
      </div>
    </div>
    <div className="card-body ">
      <span className="fw-bold">Your identity has been verified.</span>
      <div className="mt-3">
        <div className="d-flex flex-row gap-3">
          <div className="text-success-emphasis bg-success-subtle rounded-circle p-2" >
            <LuShieldCheck size={24} />
          </div>
          <p className="text-muted m-0">
            Thank you for verifying your identity. You can now continue with your application.
          </p>
        </div>
      </div>
    </div>
  </div>
)

type IdentityVerificationCardProps = StageCardProps & {
  stage: VerificationStage
}

const IdentityVerificationCard = ({
  stage,
  disabled,
  onVerify,
}: IdentityVerificationCardProps) => {
  if (stage === 'completed') {
    return <CompletedIDVerification />
  }

  if (stage === 'inProgress') {
    return <StartedIDVerification disabled={disabled} onVerify={onVerify} />
  }

  return <NoStartedIDVerification disabled={disabled} onVerify={onVerify} />
}

export default IdentityVerificationCard
